import { useState, useEffect } from 'react';
import {
  Container, Box, Paper, Typography, TextField, Button,
  Alert, CircularProgress, IconButton, InputAdornment,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../config/api';

// Política unificada con el backend: mínimo 6 caracteres y al menos un número
const isPasswordValid = (password) => password.length >= 6 && /\d/.test(password);

// Pantalla PÚBLICA a la que llega el usuario desde el correo de recuperación.
// Verifica el token al cargar y permite definir la nueva contraseña.
const ResetPasswordScreen = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const lang = i18n.language === 'en' ? 'en' : 'es';

  const token = new URLSearchParams(location.search).get('token');

  const [tokenStatus, setTokenStatus] = useState('checking'); // checking | VALID | INVALID | EXPIRED | USED
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const verify = async () => {
      if (!token) {
        setTokenStatus('INVALID');
        return;
      }
      try {
        const { data } = await api.post(`/users/password-reset/verify`, { token });
        setTokenStatus(data.response?.data?.status || 'INVALID');
      } catch (err) {
        console.error(err);
        setTokenStatus('INVALID');
      }
    };
    verify();
  }, [token]);

  const passwordOk = isPasswordValid(newPassword);
  const confirmOk = newPassword === confirmPassword;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!passwordOk || !confirmOk) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await api.post(`/users/password-reset/confirm`, {
        token,
        newPassword,
        language: lang,
      });
      setSuccess(true);
    } catch (err) {
      console.error(err);
      const code = err.response?.data?.response?.code;
      if (code && code.startsWith('TOKEN_')) {
        setTokenStatus(code.replace('TOKEN_', ''));
      } else {
        setSubmitError(err.response?.data?.response?.message || t('resetPassword.error'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const eyeAdornment = (show, setShow) => ({
    endAdornment: (
      <InputAdornment position="end">
        <IconButton onClick={() => setShow((prev) => !prev)} edge="end">
          {show ? <VisibilityOff /> : <Visibility />}
        </IconButton>
      </InputAdornment>
    ),
  });

  const renderBody = () => {
    if (tokenStatus === 'checking') {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (success) {
      return (
        <>
          <Alert severity="success" sx={{ mb: 3 }}>
            {t('resetPassword.success')}
          </Alert>
          <Button
            fullWidth
            variant="contained"
            onClick={() => navigate('/login')}
          >
            {t('resetPassword.goLogin')}
          </Button>
        </>
      );
    }

    if (tokenStatus !== 'VALID') {
      const messageKey = tokenStatus === 'EXPIRED'
        ? 'resetPassword.expired'
        : tokenStatus === 'USED'
          ? 'resetPassword.used'
          : 'resetPassword.invalid';
      return (
        <>
          <Alert severity="error" sx={{ mb: 3 }}>
            {t(messageKey)}
          </Alert>
          <Button
            fullWidth
            variant="contained"
            onClick={() => navigate('/login')}
          >
            {t('resetPassword.requestNew')}
          </Button>
        </>
      );
    }

    return (
      <Box component="form" onSubmit={handleSubmit}>
        {submitError && (
          <Alert severity="error" sx={{ mb: 2 }}>{submitError}</Alert>
        )}

        <TextField
          fullWidth
          required
          label={t('resetPassword.newPassword')}
          type={showNew ? 'text' : 'password'}
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          margin="normal"
          error={newPassword.length > 0 && !passwordOk}
          helperText={t('resetPassword.policy')}
          InputProps={eyeAdornment(showNew, setShowNew)}
        />

        <TextField
          fullWidth
          required
          label={t('resetPassword.confirmPassword')}
          type={showConfirm ? 'text' : 'password'}
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          margin="normal"
          error={confirmPassword.length > 0 && !confirmOk}
          helperText={confirmPassword.length > 0 && !confirmOk ? t('resetPassword.mismatch') : ' '}
          InputProps={eyeAdornment(showConfirm, setShowConfirm)}
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={!passwordOk || !confirmOk || submitting}
          sx={{ mt: 2, py: 1.5, fontWeight: 'bold' }}
        >
          {submitting ? t('resetPassword.submitting') : t('resetPassword.submit')}
        </Button>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f6fa',
      }}
    >
      <Container maxWidth="sm">
        <Paper elevation={5} sx={{ p: 4, borderRadius: 2 }}>
          <Typography
            variant="h5"
            component="h1"
            gutterBottom
            sx={{ color: 'primary.main', fontWeight: 600, textAlign: 'center', mb: 1 }}
          >
            {t('resetPassword.title')}
          </Typography>
          {tokenStatus === 'VALID' && !success && (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mb: 2 }}>
              {t('resetPassword.subtitle')}
            </Typography>
          )}
          {renderBody()}
        </Paper>
      </Container>
    </Box>
  );
};

export default ResetPasswordScreen;
